import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { OpenAPI } from 'axway-react-ui';
import browserHistory from 'components/utility/history';
import { generateFunctionName, getOpenAPIAuthSettings } from '../utils';

export class OASView extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
	}

	navigateTo(ev, link) {
		ev.stopPropagation();
		ev.preventDefault();
		browserHistory.push(link);
	}

	render() {
		const {
			group,
			spec,
			auth,
			triggerTypeId,
			triggeredFlows
		} = this.props;

		const createFlowText = this.intl.formatMsg('Create Flow');
		const editFlowText = this.intl.formatMsg('Edit Flow');
		const { authorization, httpConfig, apiKeyConfig } = getOpenAPIAuthSettings(auth);

		return (
			<OpenAPI
				spec={spec}
				showInfo
				authorization={authorization}
				httpConfig={httpConfig}
				apiKeyConfig={apiKeyConfig}
				examples={[ 'curl', 'node', 'web' ]}
				plugins={[
					PlugActions,
					() => ({
						afterLoad: (system) => {
							system.buttonActions.setContext({
								group,
								triggerTypeId,
								triggeredFlows,
								createFlowText,
								editFlowText,
								navigateTo: this.navigateTo
							});
						}
					})
				]}
			/>
		);
	}
}

function PlugActions() {
	// this is an *insane* amount of code for a button!!! much easier to pass
	// everything needed into a single context, rather than many individual
	// actions/reducers.
	const SET_CONTEXT = 'SET_CONTEXT';
	return {
		statePlugins: {
			button: {
				actions: {
					setContext: (payload) => ({ type: SET_CONTEXT, payload })
				},
				reducers: {
					[SET_CONTEXT]: (state, action) => {
						return state.set('context', action.payload);
					}
				},
				selectors: {
					getContext: (state) => {
						return state.get('context');
					}
				}
			}
		},
		wrapComponents: {
			OperationActions: (Original, system) => (props) => {
				const {
					operationProps,
					specPath
				} = props;
				const {
					method,
					path
				} = operationProps.toJS();

				// Check to see if this method is bound to any flows and
				// configure the Edit/Create buttons.
				const context = system.buttonSelectors.getContext();
				const {
					triggerTypeId,
					triggeredFlows
				} = context;

				// Get the path to the operation (could be via $refs)
				const paths = specPath.toJS();
				// Access the operationId from the spec
				const operationId = system.specSelectors.specJson().getIn([
					...paths,
					'operationId'
				]) || generateFunctionName(method, path);

				const flowId = triggeredFlows[operationId];
				const buttonText = (flowId)
					? context.editFlowText : context.createFlowText;
				const className = (flowId) ? 'btn edit-flow' : 'btn create-flow';
				const link = (flowId)
					? formatEditFlowLink(flowId)
					: formatCreateFlowLink({
						triggerTypeId,
						friendlyName: operationId,
						// TODO: consider renaming this in the flow-trigger as
						// `operationId` is more correct.
						method: operationId
					});

				return (
					<div className="operation-actions">
						{context && (
							<a
								className={className}
								href={link}
								onClick={ev => context.navigateTo(ev, link)}
							>
								{buttonText}
							</a>
						)}
					</div>
				);
			}
		}
	};
}

OASView.propTypes = {
	/**
	 * API spec definition (decoded)
	 */
	spec: PropTypes.object.isRequired,
	/**
	 * The auth details for the project
	 */
	auth: PropTypes.shape({
		apikey: PropTypes.string,
		// apikey, none, basic, etc...
		apiPrefixSecurity: PropTypes.string.isRequired
	}).isRequired,
	/**
	 * The flow-trigger type related to this protocol view
	 */
	triggerTypeId: PropTypes.string.isRequired,
	/**
	 * A map of configured methods that triggered flows
	 */
	triggeredFlows: PropTypes.object.isRequired
};

function formatEditFlowLink(flowId) {
	return `${window.$conf.prefix}/project/flows/${encodeURIComponent(flowId)}/edit`;
}

function formatCreateFlowLink(parameters) {
	// These `parameters` are whatever is needed to successfully configure a
	// flow-trigger.  They are specific to the needs of the flow-trigger, so
	// while we are encoding tightly-coupled 'triggerTypeId' and 'method', it is
	// okay because `OASView` is a tightly-coupled view. It is okay to provide
	// whatever we need to provide to the flow editor, so long as the flow
	// editor is able to handle them agnostically.
	let query = [];
	for (const name in parameters) {
		query.push(`${name}=${encodeURIComponent(parameters[name])}`);
	}
	return `${window.$conf.prefix}/project/flows/create?${query.join('&')}`;
}

export default injectIntl(OASView);
